import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { getErrorModuleStatus, getSize, isEmpty, isEqual, isFunction, refIsRequiredError } from "@util/utils";
import useDebouncedCallback from "@util/hook/useDebouncedCallback";
import { MASK_TYPE, Alert, CheckboxField, MenuItem, RadioField, SelectField, TextField } from "@curves/ui-base";
import { getRadioValue, setCheckboxValue } from "@icoach/documents/tools";
import useTextInputCarrier from "@icoach/components/barCode/useTextInputCarrier";
import { INVOICE_CARRIER_TYPE } from "@icoach/constants/";
import { DEFAULT_LOVE_CODE_LICHING_FOUNDATION } from "@icoach/constants/appConfig";
import useInvoiceApi from "@apis/useInvoiceApi";

const defaultDonateOptions = [
	{
		text: `勵馨基金會 ${DEFAULT_LOVE_CODE_LICHING_FOUNDATION}`,
		value: `${DEFAULT_LOVE_CODE_LICHING_FOUNDATION}`,
	},
];

const INVOICE_CARRIER_TYPE_OPTIONS = [
	{
		text: "會員載具",
		value: 1,
	},
	{
		text: "手機條碼",
		value: 2,
	},
	{
		text: "自然人憑證載具",
		value: 3,
	},
];

const INVOICE_ISSUING_TYPE_OPTIONS = [
	{
		text: "捐贈發票",
		value: 1,
	},
	{
		text: "二聯式發票",
		value: 2,
	},
	{
		text: "三聯式發票",
		value: 3,
	},
];

// 發票類型 RadioBox 切換
export const InvoiceByIssuingType = React.forwardRef(({ issuingType, errorData = {}, options = INVOICE_ISSUING_TYPE_OPTIONS, onChange: onChangeProps, mode = "radio", ...other }, ref) => {
	const issuingTypeRef = useRef(null);

	// RadioField 的 onChange: (e, value) => void
	const handleRadioChange = (e, value) => {
		isFunction(onChangeProps) && onChangeProps(value);
	};

	// SelectField 的 onChange: (e, _, value) => void
	const handleSelectChange = (e, _, value) => {
		isFunction(onChangeProps) && onChangeProps(value);
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				if (mode === "select") {
					// SelectField 直接返回 value
					return {
						issuingType: issuingTypeRef.current.getResult(),
					};
				} else {
					// RadioField 需要使用 getRadioValue 取得選中的值
					return {
						issuingType: getRadioValue(issuingTypeRef.current.getResult()),
					};
				}
			},
			isError: () => refIsRequiredError(issuingTypeRef),
		}),
		// eslint-disable-next-line
		[mode],
	);

	// 使用 SelectField 模式
	if (mode === "select") {
		return (
			<SelectField
				key={issuingType}
				label="發票類型"
				ref={issuingTypeRef}
				defaultValue={issuingType}
				{...getErrorModuleStatus(errorData, "issuingType")}
				onChange={handleSelectChange}
				fullWidth
				required
				{...other}
			>
				{options.map(({ text, value }) => (
					<MenuItem key={value} value={value}>
						{text}
					</MenuItem>
				))}
			</SelectField>
		);
	}

	// 使用 RadioField 模式（預設）
	return (
		<RadioField
			key={issuingType}
			label="發票類型"
			ref={issuingTypeRef}
			defaultValue={issuingType}
			{...getErrorModuleStatus(errorData, "issuingType")}
			onChange={handleRadioChange}
			fullWidth
			required
			row
			{...other}
		>
			{options.map(({ text, value }) => (
				<RadioField.Radio key={value} value={value} label={text} />
			))}
		</RadioField>
	);
});

// 發票備註
export const InvoiceByNotes = React.forwardRef(({ invoiceNotes, errorData = {}, onChange, ...other }, ref) => {
	const invoiceNotesRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					invoiceNotes: invoiceNotesRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(invoiceNotesRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<TextField
			key={invoiceNotes}
			label="發票備註"
			ref={invoiceNotesRef}
			defaultValue={invoiceNotes}
			{...getErrorModuleStatus(errorData, "invoiceNotes")}
			onChange={onChange}
			minRows={2}
			multiline
			fullWidth
			{...other}
		/>
	);
});

// 捐贈發票
export const InvoiceByDonate = React.forwardRef(({ donationCode = DEFAULT_LOVE_CODE_LICHING_FOUNDATION, errorData = {}, readOnly: readOnlyProps = false, mode = "radio" }, ref) => {
	const defaultValue = donationCode || DEFAULT_LOVE_CODE_LICHING_FOUNDATION;
	const isOtherValue = donationCode => {
		const index = defaultDonateOptions.findIndex(item => isEqual(item.value, donationCode));
		return index < 0;
	};
	const [showOther, setShowOther] = useState(isOtherValue(defaultValue));
	const radioFieldRef = useRef();
	const selectFieldRef = useRef();
	const donationCodeRef = useRef();

	const handleRadioValue = defaultValue => {
		if (isOtherValue(defaultValue)) {
			return "other";
		}
		return defaultValue;
	};

	const handleOtherValue = defaultValue => {
		if (isOtherValue(defaultValue)) {
			return defaultValue;
		}
		return "";
	};

	// RadioField 的 onChange: (e, value) => void
	const handleRadioChange = (e, v) => {
		setShowOther(isEqual(v, "other"));
	};

	// SelectField 的 onChange: (e, _, value) => void
	const handleSelectChange = (e, _, value) => {
		setShowOther(isEqual(value, "other"));
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};

				if (mode === "select") {
					// SelectField 模式
					if (selectFieldRef.current && selectFieldRef.current.getResult) {
						const selectedValue = selectFieldRef.current.getResult();
						if (isEqual(selectedValue, "other")) {
							// 如果選擇 "other"，使用 donationCodeRef 的值
							if (donationCodeRef.current && donationCodeRef.current.getResult) {
								result.donationCode = donationCodeRef.current.getResult();
							}
						} else {
							// 否則使用選中的值
							result.donationCode = selectedValue;
						}
					}
				} else {
					// RadioField 模式
					if (radioFieldRef.current && radioFieldRef.current.getResult) {
						result = Object.assign(result, {
							donationCode: getRadioValue(radioFieldRef.current.getResult()),
						});
					}
				}

				// 如果選擇 "other"，使用 donationCodeRef 的值（覆蓋上面的值）
				if (showOther && donationCodeRef.current && donationCodeRef.current.getResult) {
					result.donationCode = donationCodeRef.current.getResult();
				}

				return result;
			},
			isError: () => {
				if (mode === "select") {
					// SelectField 模式：如果選擇 "other"，需要檢查 donationCodeRef，否則檢查 selectFieldRef
					if (showOther) {
						return refIsRequiredError(donationCodeRef);
					} else {
						return refIsRequiredError(selectFieldRef);
					}
				} else {
					// RadioField 模式：如果選擇 "other"，需要檢查 donationCodeRef，否則檢查 radioFieldRef
					if (showOther) {
						return refIsRequiredError(donationCodeRef);
					} else {
						return refIsRequiredError(radioFieldRef);
					}
				}
			},
		}),
		// eslint-disable-next-line
		[mode, showOther],
	);

	useEffect(
		() => {
			if (donationCode) {
				setShowOther(isOtherValue(donationCode));
			}
		},
		// eslint-disable-next-line
		[donationCode],
	);

	// 準備選項（包含 "other" 選項）
	const allDonateOptions = [
		...defaultDonateOptions,
		{
			text: "其他",
			value: "other",
		},
	];

	// 使用 SelectField 模式
	if (mode === "select") {
		return (
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<SelectField
						key={handleRadioValue(defaultValue)}
						label="愛心碼"
						ref={selectFieldRef}
						defaultValue={handleRadioValue(defaultValue)}
						{...getErrorModuleStatus(errorData, "donationCode")}
						onChange={handleSelectChange}
						readOnly={readOnlyProps}
						fullWidth
						required
					>
						{allDonateOptions.map(item => (
							<MenuItem key={item.value} value={item.value}>
								{item.text}
							</MenuItem>
						))}
					</SelectField>
				</Grid>
				{showOther && (
					<Grid item xs={12}>
						<TextField
							key={donationCode}
							ref={donationCodeRef}
							label="請輸入愛心碼"
							defaultValue={handleOtherValue(defaultValue)}
							{...getErrorModuleStatus(errorData, "donationCode")}
							maskType="DC"
							readOnly={readOnlyProps}
							fullWidth
							required
						/>
					</Grid>
				)}
			</Grid>
		);
	}

	// 使用 RadioField 模式（預設）
	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<RadioField
					key={handleRadioValue(defaultValue)}
					label="愛心碼"
					ref={radioFieldRef}
					defaultValue={handleRadioValue(defaultValue)}
					{...getErrorModuleStatus(errorData, "donationCode")}
					onChange={handleRadioChange}
					readOnly={readOnlyProps}
					required
					row
				>
					{defaultDonateOptions.map(item => {
						return <RadioField.Radio key={item.value} label={item.text} value={item.value} />;
					})}
					<RadioField.Radio value="other" label="其他" />
				</RadioField>
			</Grid>
			{showOther && (
				<Grid item xs={12}>
					<TextField
						key={donationCode}
						ref={donationCodeRef}
						label="請輸入愛心碼"
						defaultValue={handleOtherValue(defaultValue)}
						{...getErrorModuleStatus(errorData, "donationCode")}
						maskType="DC"
						readOnly={readOnlyProps}
						fullWidth
						required
					/>
				</Grid>
			)}
		</Grid>
	);
});

// 手開發票
export const InvoiceByManual = React.forwardRef(({ invoiceNo, readOnly: readOnlyProps = false }, ref) => {
	const invoiceNoRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					invoiceNo: invoiceNoRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(invoiceNoRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<React.Fragment>
			<TextField label="手開發票號碼" ref={invoiceNoRef} key={invoiceNo} defaultValue={invoiceNo} readOnly={readOnlyProps} fullWidth required />
			<Alert className={"alert-justify-content-start px-0"} color={"info"} severity={"info"}>
				選擇手開發票，發票異動需由店舖自行處理。
			</Alert>
		</React.Fragment>
	);
});

// 統一編號
export const InvoiceByTaxID = React.forwardRef(({ label = "統一編號", taxID, errorData = {}, ...other }, ref) => {
	const taxIDRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					taxID: taxIDRef.current.getResult(),
				};
			},
			setError: (chk, text) => {
				taxIDRef.current.setError(chk, text);
			},
			isError: () => refIsRequiredError(taxIDRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return <TextField key={taxID} label={label} ref={taxIDRef} defaultValue={taxID} maskType="TID" {...getErrorModuleStatus(errorData, "taxID")} fullWidth required {...other} />;
});

// 發票抬頭
export const InvoiceByTaxTitle = React.forwardRef(({ label = "發票抬頭", taxTitle, errorData = {}, ...other }, ref) => {
	const taxTitleRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					taxTitle: taxTitleRef.current.getResult(),
				};
			},
			setValue: value => {
				const node = taxTitleRef.current?.node?.();
				if (node) {
					node.value = value;
				}
			},
			isError: () => refIsRequiredError(taxTitleRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return <TextField key={taxTitle} label={label} ref={taxTitleRef} defaultValue={taxTitle} {...getErrorModuleStatus(errorData, "taxTitle")} fullWidth required {...other} />;
});

// 發票載具
export const InvoiceByCarrierType = React.forwardRef(({ carrierType, options = INVOICE_CARRIER_TYPE_OPTIONS, errorData = {}, onChange: onChangeProps, ...other }, ref) => {
	const carrierTypeOptions = options || [];
	const carrierTypeRef = useRef();

	const handleChange = (e, _, value) => {
		isFunction(onChangeProps) && onChangeProps(value);
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					carrierType: carrierTypeRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(carrierTypeRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<SelectField
			label="發票載具"
			ref={carrierTypeRef}
			key={carrierType}
			defaultValue={carrierType}
			{...getErrorModuleStatus(errorData, "invoiceCarrierType")}
			onChange={handleChange}
			fullWidth
			required
			{...other}
		>
			{Array.isArray(carrierTypeOptions) &&
				carrierTypeOptions
					.filter(item => item || !item.disabled)
					.map(item => (
						<MenuItem key={item.value} value={item.value}>
							{item.text}
						</MenuItem>
					))}
		</SelectField>
	);
});

// 手機條碼載具號碼欄位（含掃描功能）
const MobileCarrierNoField = React.forwardRef(({ carrierNo, errorData = {}, readOnly: readOnlyProps = false, ...other }, ref) => {
	const carrierNoRef = useRef();
	const [scannedValue, setScannedValue] = useState(null);
	const { renderEndAdornment, renderDialog } = useTextInputCarrier(setScannedValue, { maskType: MASK_TYPE.MB, readOnly: readOnlyProps });

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					carrierNo: carrierNoRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(carrierNoRef),
		}),
		// eslint-disable-next-line
		[],
	);
	return (
		<>
			<TextField
				label="載具號碼"
				ref={carrierNoRef}
				key={`${carrierNo}-${scannedValue}`}
				defaultValue={scannedValue ?? carrierNo}
				{...getErrorModuleStatus(errorData, "invoiceCarrierNo")}
				maskType={MASK_TYPE.MB}
				InputProps={{ endAdornment: renderEndAdornment }}
				readOnly={readOnlyProps}
				fullWidth
				required
				{...other}
			/>
			{renderDialog}
		</>
	);
});

// 自然人憑證載具號碼欄位
const CdcCarrierNoField = React.forwardRef(({ carrierNo, errorData = {}, readOnly: readOnlyProps = false, ...other }, ref) => {
	const carrierNoRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					carrierNo: carrierNoRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(carrierNoRef),
		}),
		// eslint-disable-next-line
		[],
	);
	return (
		<TextField
			label="載具號碼"
			ref={carrierNoRef}
			key={carrierNo}
			defaultValue={carrierNo}
			{...getErrorModuleStatus(errorData, "invoiceCarrierNo")}
			maskType={MASK_TYPE.CDC}
			readOnly={readOnlyProps}
			fullWidth
			required
			{...other}
		/>
	);
});

// 載具號碼（純切換器）
export const InvoiceByCarrierNo = React.forwardRef(({ carrierNo, carrierType, errorData = {}, readOnly: readOnlyProps = false, ...other }, ref) => {
	if (isEqual(carrierType, INVOICE_CARRIER_TYPE.MOBILEBARCODE)) {
		return <MobileCarrierNoField ref={ref} carrierNo={carrierNo} errorData={errorData} readOnly={readOnlyProps} {...other} />;
	}
	return <CdcCarrierNoField ref={ref} carrierNo={carrierNo} errorData={errorData} readOnly={readOnlyProps} {...other} />;
});

// 載具信箱
export const InvoiceByEmail = React.forwardRef(({ label = "寄送載具信箱", issuingEmail, errorData = {}, ...other }, ref) => {
	const issuingEmailRef = useRef(null);
	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					issuingEmail: issuingEmailRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(issuingEmailRef),
		}),
		// eslint-disable-next-line
		[],
	);
	return (
		<TextField
			key={issuingEmail}
			ref={issuingEmailRef}
			label={label}
			maskType={MASK_TYPE.EMAIL}
			defaultValue={issuingEmail}
			{...getErrorModuleStatus(errorData, "issuingEmail")}
			fullWidth
			{...other}
		/>
	);
});

// 載具手機
export const InvoiceByPhone = React.forwardRef(({ label = "寄送載具手機", issuingPhone, errorData = {}, readOnly: readOnlyProps = false, ...other }, ref) => {
	const issuingPhoneRef = useRef(null);
	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					issuingPhone: issuingPhoneRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(issuingPhoneRef),
		}),
		// eslint-disable-next-line
		[],
	);
	return (
		<TextField
			key={issuingPhone}
			ref={issuingPhoneRef}
			label={label}
			defaultValue={issuingPhone}
			{...getErrorModuleStatus(errorData, "issuingPhone")}
			maskType="MOB"
			readOnly={readOnlyProps}
			fullWidth
			{...other}
		/>
	);
});

// 同步更新會員電子郵件與發票
export const SynchronizeMemberInvoice = React.forwardRef(({ synchronizeMemberInvoiceValue, readOnly: readOnlyProps = false, className }, ref) => {
	const synchronizeMemberInvoiceRef = useRef();

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				const synchronizeMemberInvoiceValue = synchronizeMemberInvoiceRef.current && synchronizeMemberInvoiceRef.current.getResult();
				const result = {};

				if (Array.isArray(synchronizeMemberInvoiceValue)) {
					synchronizeMemberInvoiceValue.forEach(item => {
						result[item.value] = item.checked;
					});
				}

				return result;
			},
			isError: () => refIsRequiredError(synchronizeMemberInvoiceRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<CheckboxField
			key={synchronizeMemberInvoiceValue}
			ref={synchronizeMemberInvoiceRef}
			className={className}
			defaultValue={setCheckboxValue(synchronizeMemberInvoiceValue)}
			readOnly={readOnlyProps}
		>
			<CheckboxField.Checkbox label="同步更新會員電子郵件" value="syncUpgradeMemberEmail" />
			<CheckboxField.Checkbox label="同步更新會員發票載具類型" value="syncUpgradeMemberCarrier" />
		</CheckboxField>
	);
});

// 三聯發票
export const InvoiceByTriplicateUniform = React.forwardRef(({ taxID = "", taxTitle = "", errorData = {}, readOnly = false }, ref) => {
	const taxIDRef = useRef(null);
	const taxTitleRef = useRef(null);
	const { getInvoiceTaxCompanyApi } = useInvoiceApi();

	const handleChangeTaxID = e => {
		if (!isEmpty(e.target.value) && getSize(e.target.value.trim()) === 8) {
			debouncedChangeTaxID(e.target.value);
		}
	};

	const debouncedChangeTaxID = useDebouncedCallback(taxID => {
		doQueryTaxCompany(taxID);
	}, 500);

	const doQueryTaxCompany = async taxID => {
		const resp = await getInvoiceTaxCompanyApi(taxID);

		if (resp) {
			const { isSuccess, errorMessage, taxName } = resp;

			if (!isSuccess) {
				taxIDRef.current.setError(true, errorMessage);
				taxTitleRef.current.setValue(null);
			} else {
				taxIDRef.current.setError(false, null);
				if (!isEmpty(taxName)) {
					taxTitleRef.current.setValue(taxName);
				}
			}
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				let result = {};

				if (taxIDRef.current && taxIDRef.current.getResult) {
					Object.assign(result, taxIDRef.current.getResult());
				}

				if (taxTitleRef.current && taxTitleRef.current.getResult) {
					Object.assign(result, taxTitleRef.current.getResult());
				}

				return result;
			},
			isError: () => refIsRequiredError(taxIDRef, taxTitleRef),
		}),
		// eslint-disable-next-line
		[],
	);

	return (
		<React.Fragment>
			<Grid item xs={12}>
				<InvoiceByTaxID ref={taxIDRef} taxID={taxID} errorData={errorData} readOnly={readOnly} onChange={handleChangeTaxID} />
			</Grid>
			<Grid item xs={12}>
				<InvoiceByTaxTitle ref={taxTitleRef} taxTitle={taxTitle} errorData={errorData} readOnly={readOnly} />
			</Grid>
		</React.Fragment>
	);
});
